/**
 * Header – glavna navigaciona traka i korisnički meni (Container/Presenter).
 * Logika u useHeaderLogic; UI u DesktopNav, MobileNav, UserDropdown.
 */
import React, { useEffect } from 'react';
import LoginLaunchLink from '@/shared/components/auth/LoginLaunchLink';
import { useMaxWidthMediaQuery } from '@/shared/hooks/useMaxWidthMediaQuery';
import { useHeaderLogic } from './header/useHeaderLogic';
import { getProfileIcon } from './header/ProfileTypeIcons';
import UserSettingsModal from '../modals/UserSettingsModal';
import DesktopNav from './header/DesktopNav';
import MobileNav from './header/MobileNav';
import UserDropdown from './header/UserDropdown';
import './Header.css';

const MOBILE_HEADER_MAX_WIDTH = 1180;
const GUEST_HIDE_BURGER_MAX_WIDTH = 455;

const Header = ({
  homeSectionNavDesktop = null,
  homeSectionBurgerLinks = null,
  showHomeSectionBurgerLinks = false,
  onModeratorAssignmentChanged,
}) => {
  const isMobileHeader = useMaxWidthMediaQuery(MOBILE_HEADER_MAX_WIDTH);
  const isGuestNarrowScreen = useMaxWidthMediaQuery(GUEST_HIDE_BURGER_MAX_WIDTH);
  const { state, actions } = useHeaderLogic();
  const signInOffset = state.isUserLoggedIn ? '' : 'signIn';
  const myEgovShow = state.isUserLoggedIn ? 'my-egov' : 'my-egov d-none';
  const signInBarShow = !state.isUserLoggedIn ? 'signin-bar' : 'signin-bar d-none';
  const profileIcon = getProfileIcon(state.activeRole, state.userType, 16);

  const homeSectionsInBurger =
    showHomeSectionBurgerLinks && (state.isUserLoggedIn || !isGuestNarrowScreen);
  const showBurgerMenu = (state.isUserLoggedIn && isMobileHeader) || homeSectionsInBurger;

  useEffect(() => {
    if (!state.isUserLoggedIn && isGuestNarrowScreen && state.isMenuOpen) {
      actions.closeBurger();
    }
  }, [state.isUserLoggedIn, isGuestNarrowScreen, state.isMenuOpen, actions]);
  const headerClassName = [
    signInOffset,
    homeSectionsInBurger ? 'header--home-nav-burger' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      <UserSettingsModal isOpen={state.showSettingsModal} onClose={() => actions.setShowSettingsModal(false)} />
      <header className={headerClassName} role="banner">
        <div className="head-sections">
          <div className="mid-bar">
            <DesktopNav homeSectionNavSlot={homeSectionNavDesktop} />
            <div className="header-user-menu" role="navigation" aria-label="Кориснички мени">
              {showBurgerMenu && (
                <button
                  type="button"
                  className={`mobile-handle header-burger-btn ${state.isMenuOpen ? 'header-burger-btn--close notification-indicator-warning-chip' : ''}`}
                  onClick={(e) => { e.preventDefault(); actions.toggleBurger(); }}
                  aria-label={state.isMenuOpen ? undefined : 'Отвори мени'}
                  aria-labelledby={state.isMenuOpen ? 'header-burger-close-label' : undefined}
                  aria-expanded={state.isMenuOpen}
                  aria-controls="mobile-nav-menu"
                >
                  {!state.isMenuOpen && (
                    <svg
                      aria-hidden="true"
                      className="header-burger-icon"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    >
                      <line x1="4" y1="7" x2="20" y2="7" />
                      <line x1="4" y1="12" x2="20" y2="12" />
                      <line x1="4" y1="17" x2="20" y2="17" />
                    </svg>
                  )}
                  {state.isMenuOpen && (
                    <>
                      <svg aria-hidden="true" className="notification-indicator-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                      <span id="header-burger-close-label" className="notification-indicator-label">
                        Затвори мени
                      </span>
                    </>
                  )}
                </button>
              )}
              {!state.isUserLoggedIn && (
                <div className={`${signInBarShow} header-signin-bar`}>
                  <LoginLaunchLink className="signin-button create-button">
                    Пријави се
                  </LoginLaunchLink>
                </div>
              )}
              <UserDropdown
                user={state.user}
                isUserLoggedIn={state.isUserLoggedIn}
                activeRole={state.activeRole}
                userType={state.userType}
                selectedOrganizationName={state.selectedOrganizationName}
                displayName={state.displayName}
                unreadCount={state.unreadCount}
                isProfileOpen={state.isProfileOpen}
                onProfileBlur={actions.handleProfileBlur}
                onProfileLinkClick={actions.closeProfileAndNavigate}
                onLogout={actions.handleLogoutClick}
                onSettingsClick={actions.handleSettingsClick}
                onRoleChange={actions.setActiveRoleAndPersist}
                onNavigateAdmin={actions.closeProfileAndNavigate}
                onMenuListClick={actions.closeProfileOnMenuClick}
                profileBlockClassName={myEgovShow}
                onModeratorAssignmentChanged={onModeratorAssignmentChanged}
                onUnreadCountSync={actions.syncUnreadCount}
              />
              {showBurgerMenu && (
                <MobileNav
                  id="mobile-nav-menu"
                  activeRole={state.activeRole}
                  displayName={state.displayName}
                  profileIcon={profileIcon}
                  showUserItems={state.isUserLoggedIn && isMobileHeader}
                  showHomeSectionLinks={homeSectionsInBurger}
                  homeSectionBurgerSlot={homeSectionBurgerLinks}
                  onCloseBurger={actions.closeBurger}
                  onLogout={actions.handleLogoutClick}
                />
              )}
            </div>
          </div>
          <div className="bot-bar" aria-hidden="true" />
        </div>
      </header>
    </>
  );
};

export default Header;
